import React, { useState, useMemo } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { withPageProtection } from '@/lib/rbac/hoc'
import { PAGES } from '@/lib/rbac/constants'
import { MetricCard } from '@/components/MetricCard'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { HTMLSelect as Select } from '@/components/ui/Select'
import { Card } from '@/components/ui/Card'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { EmptyState } from '@/components/ui/EmptyState'
import { Badge } from '@/components/ui/Badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs'
import { usePayroll, useEmployees } from '@/hooks/useWorkforce'
import { useQuery } from '@tanstack/react-query'
import { getPayrollTotalsByPeriod } from '@/api/workforce'
import { useTenant } from '@/hooks/useTenant'
import { PayrollCost, Employee } from '@/types/workforce'
import { Link } from '@tanstack/react-router'
import {
  DollarSign,
  Plus,
  Search,
  Calendar,
  TrendingUp,
  TrendingDown,
  Users,
  Calculator,
  Download,
  FileText,
  Filter,
  MoreHorizontal,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw
} from 'lucide-react'

function getCurrentPeriod() {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
}

function PayrollContent() {
  const { user } = useAuth()
  const { tenantDomain } = useTenant()

  // 🎯 Local State (React useState for filters)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedPeriod, setSelectedPeriod] = useState(getCurrentPeriod())
  const [selectedType, setSelectedType] = useState('')
  const [selectedEmployee, setSelectedEmployee] = useState('')

  // Convert period (YYYY-MM) to date range
  const dateRange = useMemo(() => {
    const [year, month] = selectedPeriod.split('-')
    const startDate = `${year}-${month}-01`
    const endDate = new Date(parseInt(year), parseInt(month), 0).toISOString().split('T')[0] // Last day of month
    return { startDate, endDate }
  }, [selectedPeriod])

  // 🚀 Server State (TanStack Query)
  const {
    payrollCosts,
    loading: payrollLoading,
    isFetching: payrollIsFetching,
    error: payrollError
  } = usePayroll(
    {
      startDate: dateRange.startDate,
      endDate: dateRange.endDate
    },
    {}
  )

  const {
    employees,
    loading: employeesLoading,
    isFetching: employeesIsFetching
  } = useEmployees()

  const { data: totals, isLoading: totalsLoading, isFetching: totalsIsFetching } = useQuery({
    queryKey: ['workforce', 'payroll', 'totals', tenantDomain, dateRange.startDate, dateRange.endDate],
    queryFn: async () => {
      return await getPayrollTotalsByPeriod(tenantDomain, {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      })
    },
    enabled: !!tenantDomain,
  })

  // Derived state
  const loading = payrollLoading || employeesLoading || totalsLoading
  const isFetching = payrollIsFetching || employeesIsFetching || totalsIsFetching

  const formatCurrency = (amount?: number) => {
    if (!amount) return 'R$ 0,00'
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(amount)
  }

  const formatDate = (date: Date | string) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(new Date(date))
  }

  const formatPeriod = (period: string) => {
    const [year, month] = period.split('-')
    const monthNames = [
      'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
    ]
    return `${monthNames[parseInt(month) - 1]} ${year}`
  }

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'salary':
        return <DollarSign className="w-4 h-4 text-brand-primary" />
      case 'bonus':
        return <ArrowUpRight className="w-4 h-4 text-success" />
      case 'overtime':
        return <TrendingUp className="w-4 h-4 text-warning" />
      case 'deduction':
        return <ArrowDownRight className="w-4 h-4 text-error" />
      case 'tax':
        return <Calculator className="w-4 h-4 text-brand-gray" />
      default:
        return <FileText className="w-4 h-4 text-brand-gray" />
    }
  }

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'salary':
        return 'bg-brand-primary/20 text-brand-primary'
      case 'bonus':
        return 'bg-success/20 text-success'
      case 'overtime':
        return 'bg-warning/20 text-warning'
      case 'deduction':
        return 'bg-error/20 text-error'
      case 'tax':
        return 'bg-brand-gray/20 text-brand-gray'
      default:
        return 'bg-brand-gray/20 text-brand-gray'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-success/20 text-success'
      case 'processed':
        return 'bg-brand-primary/20 text-brand-primary'
      case 'draft':
        return 'bg-warning/20 text-warning'
      case 'cancelled':
        return 'bg-error/20 text-error'
      default:
        return 'bg-brand-gray/20 text-brand-gray'
    }
  }

  const filteredCosts = payrollCosts.filter(cost => {
    let matches = true

    if (searchTerm) {
      const employee = employees.find(emp => emp.employeeId === cost.employeeId)
      const search = searchTerm.toLowerCase()
      matches = matches && (
        employee?.personalInfo.name.toLowerCase().includes(search) ||
        cost.description?.toLowerCase().includes(search) ||
        cost.category.toLowerCase().includes(search)
      )
    }

    if (selectedType) {
      matches = matches && cost.type === selectedType
    }

    if (selectedEmployee) {
      matches = matches && cost.employeeId === selectedEmployee
    }

    return matches
  })

  const groupedCosts = filteredCosts.reduce((acc, cost) => {
    const employee = employees.find(emp => emp.employeeId === cost.employeeId)
    const key = employee ? `${employee.personalInfo.name.split(" ")[0]} ${employee.personalInfo.name.split(" ").slice(1).join(" ")}` : 'Colaborador não encontrado'

    if (!acc[key]) {
      acc[key] = {
        employee,
        costs: [],
        totalGross: 0,
        totalNet: 0
      }
    }

    acc[key].costs.push(cost)
    acc[key].totalGross += cost.grossAmount || cost.amount
    acc[key].totalNet += cost.netAmount || cost.amount

    return acc
  }, {} as Record<string, any>)

  return (
    <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-white mb-2">
              Folha de Pagamento
            </h1>
            <p className="font-body text-brand-gray">
              Gerencie custos de folha de pagamento e relatórios financeiros
            </p>
          </div>
          <div className="mt-4 sm:mt-0 flex space-x-3">
            <Button variant="ghost" size="sm">
              <Download className="w-4 h-4 mr-2" />
              Exportar
            </Button>
            <Link to={"/workforce/payroll/new" as any}>
              <Button size="sm">
                <Plus className="w-4 h-4 mr-2" />
                Novo Lançamento
              </Button>
            </Link>
          </div>
        </div>

        {/* Period Selector */}
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-display text-lg font-semibold text-white mb-2">
                Período Selecionado
              </h3>
              <p className="text-brand-gray">
                {formatPeriod(selectedPeriod)}
              </p>
            </div>
            <div className="flex items-center gap-4">
              <Select
                value={selectedPeriod}
                onValueChange={setSelectedPeriod}
              >
                {Array.from({ length: 12 }, (_, i) => {
                  const date = new Date()
                  date.setMonth(date.getMonth() - i)
                  const period = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
                  return (
                    <option key={period} value={period}>
                      {formatPeriod(period)}
                    </option>
                  )
                })}
              </Select>
            </div>
          </div>
        </Card>

        {/* Stats Cards */}
        {totals && (
          <div className="dashboard-grid lg:grid-cols-4">
            <MetricCard
              title="Custo Total"
              value={formatCurrency((totals as any).totalCosts ?? totals.totalGrossSalary)}
              icon={DollarSign}
              trend="neutral"
            />
            <MetricCard
              title="Custo Médio"
              value={formatCurrency((totals as any).averageCost ?? (totals.totalGrossSalary / (totals.employeeCount || 1)))}
              icon={Calculator}
              trend="neutral"
            />
            <MetricCard
              title="Colaboradores"
              value={totals.employeeCount}
              icon={Users}
              trend="neutral"
            />
            <MetricCard
              title="Lançamentos"
              value={payrollCosts.length}
              icon={FileText}
              trend="up"
            />
          </div>
        )}

        {/* Cost Breakdown */}
        {totals && (totals as any).breakdown && (
          <Card className="p-6">
            <h3 className="font-display text-lg font-semibold text-white mb-4">
              Composição dos Custos
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {(totals as any).breakdown.map((item: any, index: number) => (
                <div key={index} className="text-center">
                  <div className={`p-4 rounded-monumenta ${getTypeColor(item.type)} border border-current`}>
                    <div className="flex justify-center mb-2">
                      {getTypeIcon(item.type)}
                    </div>
                    <div className="font-semibold text-sm">{formatCurrency(item.amount)}</div>
                    <div className="text-xs capitalize mt-1">{item.type}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Tabs for different views */}
        <Tabs defaultValue="by-employee" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="by-employee">Por Colaborador</TabsTrigger>
            <TabsTrigger value="all-entries">Todos os Lançamentos</TabsTrigger>
          </TabsList>

          {/* Filters */}
          <Card className="p-6">
            <div className="flex flex-col lg:flex-row lg:items-center gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-brand-gray pointer-events-none z-10" />
                  <Input
                    placeholder="Buscar por colaborador, descrição ou categoria..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    style={{ paddingLeft: '3rem' }}
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <Select
                  value={selectedEmployee}
                  onValueChange={setSelectedEmployee}
                >
                  <option value="">Todos os colaboradores</option>
                  {employees.map(emp => (
                    <option key={emp.employeeId} value={emp.employeeId}>
                      {emp.personalInfo.name.split(" ")[0]} {emp.personalInfo.name.split(" ").slice(1).join(" ")}
                    </option>
                  ))}
                </Select>
                <Select
                  value={selectedType}
                  onValueChange={setSelectedType}
                >
                  <option value="">Todos os tipos</option>
                  <option value="salary">Salário</option>
                  <option value="bonus">Bônus</option>
                  <option value="overtime">Hora Extra</option>
                  <option value="commission">Comissão</option>
                  <option value="deduction">Desconto</option>
                  <option value="tax">Imposto</option>
                  <option value="benefit">Benefício</option>
                </Select>
              </div>
            </div>
          </Card>

          {/* 🚀 Background refresh indicator (subtle, non-blocking) */}
          {isFetching && !loading && (
            <div className="flex items-center gap-2 text-xs text-gray-400 bg-gray-800 px-3 py-1.5 rounded-md">
              <RefreshCw className="w-3 h-3 animate-spin" />
              <span>Sincronizando...</span>
            </div>
          )}

          <TabsContent value="by-employee" className="space-y-6">
            {loading ? (
              <div className="flex justify-center py-12">
                <LoadingSpinner size="lg" />
              </div>
            ) : Object.keys(groupedCosts).length === 0 ? (
              <EmptyState
                icon={DollarSign}
                title="Nenhum lançamento encontrado"
                description="Não há lançamentos de folha de pagamento para o período selecionado."
                action={
                  <Link to={"/workforce/payroll/new" as any}>
                    <Button>
                      <Plus className="w-4 h-4 mr-2" />
                      Primeiro Lançamento
                    </Button>
                  </Link>
                }
              />
            ) : (
              <div className="space-y-4">
                {Object.entries(groupedCosts).map(([employeeName, data]) => (
                  <Card key={employeeName} className="card-hover p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-brand-primary to-brand-accent-600 rounded-full flex items-center justify-center">
                          <span className="text-sm font-bold text-brand-dark">
                            {data.employee ? `${data.employee.personalInfo.name.split(" ")[0][0]}${data.employee.personalInfo.name.split(" ").slice(1).join(" ")[0]}` : '??'}
                          </span>
                        </div>
                        <div>
                          <h3 className="font-display text-lg font-semibold text-white">
                            {employeeName}
                          </h3>
                          <div className="flex items-center gap-4 text-sm text-brand-gray">
                            <span>Bruto: {formatCurrency(data.totalGross)}</span>
                            <span>Líquido: {formatCurrency(data.totalNet)}</span>
                            <span>{data.costs.length} lançamentos</span>
                          </div>
                        </div>
                      </div>
                      <Link to={`/workforce/employees/${data.employee?.id}/payroll` as any}>
                        <Button variant="ghost" size="sm">
                          Ver Detalhes
                        </Button>
                      </Link>
                    </div>

                    <div className="grid gap-3">
                      {data.costs.slice(0, 3).map((cost: PayrollCost, index: number) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-brand-dark-lighter rounded-monumenta">
                          <div className="flex items-center gap-3">
                            {getTypeIcon(cost.type)}
                            <div>
                              <div className="font-medium text-white">{cost.category}</div>
                              <div className="text-sm text-brand-gray">{cost.description}</div>
                            </div>
                            <Badge className={getTypeColor(cost.type)}>
                              {cost.type}
                            </Badge>
                          </div>
                          <div className="text-right">
                            <div className="font-semibold text-white">{formatCurrency(cost.amount)}</div>
                            <Badge className={getStatusColor(cost.status)}>
                              {cost.status}
                            </Badge>
                          </div>
                        </div>
                      ))}
                      {data.costs.length > 3 && (
                        <div className="text-center text-sm text-brand-gray">
                          +{data.costs.length - 3} lançamentos adicionais
                        </div>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="all-entries" className="space-y-6">
            {loading ? (
              <div className="flex justify-center py-12">
                <LoadingSpinner size="lg" />
              </div>
            ) : filteredCosts.length === 0 ? (
              <EmptyState
                icon={DollarSign}
                title="Nenhum lançamento encontrado"
                description="Não há lançamentos que correspondam aos critérios de busca."
              />
            ) : (
              <div className="space-y-3">
                {filteredCosts.map((cost) => {
                  const employee = employees.find(emp => emp.employeeId === cost.employeeId)
                  return (
                    <Card key={cost.payrollId} className="card-hover p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 flex-1">
                          {getTypeIcon(cost.type)}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3 mb-1">
                              <span className="font-medium text-white">{cost.category}</span>
                              <Badge className={getTypeColor(cost.type)}>
                                {cost.type}
                              </Badge>
                              <Badge className={getStatusColor(cost.status)}>
                                {cost.status}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-4 text-sm text-brand-gray">
                              <span>{employee ? `${employee.personalInfo.name.split(" ")[0]} ${employee.personalInfo.name.split(" ").slice(1).join(" ")}` : 'N/A'}</span>
                              <span>{formatDate(cost.processedDate)}</span>
                              {cost.description && <span>{cost.description}</span>}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <div className="font-semibold text-brand-primary">{formatCurrency(cost.amount)}</div>
                            {cost.netAmount && cost.netAmount !== cost.amount && (
                              <div className="text-sm text-brand-gray">Líquido: {formatCurrency(cost.netAmount)}</div>
                            )}
                          </div>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  )
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
  )
}

// Apply RBAC protection - only admin can access workforce payroll
export default withPageProtection(PayrollContent, PAGES.WORKFORCE_PAYROLL)